export enum CompanionMessageType {
  Plain = 'plain',
  Form = 'form',
  FormResolved = 'form_resolved',
  ConfirmationRequired = 'confirmation_required',
  ConfirmationResolved = 'confirmation_resolved',
}

export enum CompanionFormFieldType {
  Text = 'text',
  Reference = 'entity_reference',
  DateTimeUtc = 'date_time_utc',
}

export enum CompanionReferenceType {
  OrganizationUnit = 'OrganizationUnit',
  BotConfiguration = 'BotConfiguration',
  VirtualUser = 'VirtualUser',
  Team = 'Team',
  Tenant = 'Tenant',
}

export const CompanionReferenceTypesCascade: CompanionReferenceType[] = [CompanionReferenceType.OrganizationUnit];

export enum CompanionFormOperation {
  Create = 'create',
  Update = 'update',
  Read = 'read',
}

export interface CompanionFormField {
  name: string;
  description?: string;
  type: CompanionFormFieldType;
  reference?: CompanionReferenceType;
  required: boolean;
  secret: boolean;
}

export interface CompanionForm {
  entity: CompanionReferenceType;
  operation: CompanionFormOperation;
  fields: CompanionFormField[];
  reason?: string;
}

export interface CompanionFormModel {
  requestId: string;
  form?: CompanionForm;
  values: Record<string, string>;
}

export interface CompanionConfirmation {
  toolName: string;
  arguments?: Record<string, unknown>;
}

export enum CompanionConfirmationResult {
  Approved = 'approved',
  Rejected = 'rejected',
}

export interface CompanionConfirmationModel {
  requestId: string;
  decision: CompanionConfirmationResult;
  declineReason?: string;
}

export interface CompanionClientHints {
  routeTemplate: string;
  referralSource?: string;
}

export interface CompanionConversationMessageModel {
  query?: string;
  formSubmission?: CompanionFormModel;
  confirmationResponse?: CompanionConfirmationModel;
  clientHints: CompanionClientHints;
}

export interface CompanionConversationModel {
  internalStatus: CompanionInternalStatus;
  errorCode?: CompanionErrorCode;
  message: CompanionConversationDataModel;
  conversationId: string;
  requestId: string;
}

export interface CompanionConversationDataModel {
  content: string;
  tools?: CompanionTool[];
  toolCalls?: CompanionToolCallRecord[];
  messageType?: CompanionMessageType;
  requestId?: string;
  form?: CompanionForm;
  confirmation?: CompanionConfirmation;
  formSubmission?: CompanionFormModel;
  confirmationSubmission?: CompanionConfirmationModel;
  error?: CompanionMessageError;
}

export enum CompanionToolErrorCode {
  InvalidInput = 'INVALID_INPUT',
  Unavailable = 'UNAVAILABLE',
  Timeout = 'TIMEOUT',
  McpError = 'MCP_ERROR',
  Unauthorized = 'UNAUTHORIZED',
  Forbidden = 'FORBIDDEN',
  LicenseLimitExceeded = 'LICENSE_LIMIT_EXCEEDED',
  Unknown = 'UNKNOWN',
  InternalError = 'INTERNAL_ERROR',
}

export interface CompanionMessageError {
  code: CompanionToolErrorCode | string;
  message: string;
}

export interface CompanionToolCallRecord {
  toolName: string;
  metadata?: Record<string, unknown>;
  error?: CompanionMessageError;
}

export interface CompanionDocumentSourceModel {
  title: string;
  url: string;
  score: number;
}

export enum CompanionInternalStatus {
  Success = 'Success',
  Failure = 'Failure',
}

export enum CompanionErrorCode {
  NotFound = 'NotFound',
  RateLimited = 'RateLimited',
  BadRequest = 'BadRequest',
  InternalError = 'InternalError',
  UnknownError = 'UnknownError',
}

export enum CompanionTool {
  Help = 'help_tool',
  Status = 'status_tool',
  PageIndex = 'page_index_tool',
  EnvironmentInfo = 'environment_info_tool',
}

export interface ConversationSummaryModel {
  conversationId: string;
  title: string;
  updatedAt: string;
}

export interface ConversationsResponseModel {
  conversations: ConversationSummaryModel[];
  continuationToken?: string;
}

export interface ConversationMessageModel {
  messageId: string;
  requestId?: string;
  role: string;
  content: string;
  toolCalls?: CompanionToolCallRecord[];
  tools?: string[];
  createdAt: string;
  messageType?: CompanionMessageType;
  form?: CompanionForm;
  confirmation?: CompanionConfirmation;
  formSubmission?: CompanionFormModel;
  confirmationSubmission?: CompanionConfirmationModel;
  error?: CompanionMessageError;
}

export interface ConversationMessagesResponseModel {
  conversationId: string;
  messages: ConversationMessageModel[];
  continuationToken?: string;
}
